import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Badge } from "../ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, tone = "stone" }) => (
  <div className={`p-3 rounded-lg border bg-${tone}-50 border-${tone}-200 text-center`}>
    <p className="text-2xl font-bold text-stone-900">{value}</p>
    <p className="text-xs text-stone-500">{label}</p>
  </div>
);

const EtifoodImportDialog = ({ open, onClose, onImported }) => {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => { setStep(1); setPreview(null); setResult(null); setBusy(false); };
  const close = () => { reset(); onClose(); };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/warehouse/inventory/import-etifood/preview`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setPreview(r.data);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella lettura del file");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/warehouse/inventory/import-etifood/confirm`, { session_id: preview.session_id }, { timeout: 180000 });
      setResult(r.data);
      setStep(3);
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'import");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-[#8A1C2A]" />Importa storico da Etifood</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <p className="text-sm text-stone-600">Carica l'export Excel di Etifood (<span className="font-mono">CARICO MAGAZZINO.XLS</span>). Verranno importati carichi, partite e barcode collo, con creazione automatica dei fornitori mancanti.</p>
            <label className="flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed border-stone-300 rounded-xl cursor-pointer hover:border-[#8A1C2A]/50 hover:bg-stone-50 transition-colors" data-testid="etifood-dropzone">
              <Upload className="w-8 h-8 text-stone-400" />
              <span className="text-sm font-medium text-stone-700">{busy ? "Lettura del file…" : "Clicca per scegliere il file .xls / .xlsx"}</span>
              <input type="file" accept=".xls,.xlsx" className="hidden" disabled={busy} onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="etifood-file-input" />
            </label>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatCard label="Carichi trovati" value={preview.shipments_count} />
              <StatCard label="Righe / colli" value={preview.rows_count} />
              <StatCard label="Kg totali" value={preview.total_kg.toLocaleString("it-IT")} />
              <StatCard label="Già importati" value={preview.already_imported} />
            </div>
            {preview.unmatched_products?.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm" data-testid="etifood-unmatched-warning">
                <p className="font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle className="w-4 h-4" />{preview.unmatched_products.length} codici articolo non presenti in catalogo</p>
                <p className="text-amber-700 mt-1">Le partite saranno importate comunque con codice e descrizione del file (senza collegamento al prodotto): {preview.unmatched_products.slice(0, 8).join(", ")}{preview.unmatched_products.length > 8 ? "…" : ""}</p>
              </div>
            )}
            {preview.new_suppliers?.length > 0 && (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm">
                <p className="font-semibold text-blue-800">🆕 {preview.new_suppliers.length} fornitori verranno creati:</p>
                <p className="text-blue-700 mt-1">{preview.new_suppliers.join(", ")}</p>
              </div>
            )}
            <div className="border border-stone-200 rounded-lg overflow-hidden">
              <div className="max-h-64 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">N.</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Data</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">Fornitore</th>
                      <th className="text-left p-2 text-xs font-semibold text-stone-600">DDT</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">Righe</th>
                      <th className="text-right p-2 text-xs font-semibold text-stone-600">Kg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.shipments.map((s) => (
                      <tr key={s.number} className={`border-t border-stone-100 ${s.already_imported ? "opacity-40" : ""}`} data-testid={`etifood-preview-row-${s.number}`}>
                        <td className="p-2 font-semibold">{s.number}{s.already_imported && <Badge variant="outline" className="ml-1 text-[10px]">GIÀ IMPORTATO</Badge>}</td>
                        <td className="p-2">{s.date?.split("-").reverse().join("/")}</td>
                        <td className="p-2 truncate max-w-[140px]">{s.supplier_name}</td>
                        <td className="p-2">{s.ddt_reference}</td>
                        <td className="p-2 text-right">{s.rows}</td>
                        <td className="p-2 text-right">{s.total_kg.toLocaleString("it-IT")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="w-6 h-6" /><p className="font-semibold">Import completato</p></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="etifood-result-cards">
              <StatCard label="Carichi creati" value={result.shipments_created} />
              <StatCard label="Partite create" value={result.lots_created} />
              <StatCard label="Fornitori creati" value={result.suppliers_created} />
              <StatCard label="Carichi saltati (già presenti)" value={result.shipments_skipped} />
              <StatCard label="Partite saltate (barcode esistente)" value={result.lots_skipped} />
              <StatCard label="Codici non in catalogo" value={result.products_unmatched} />
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 2 && <Button variant="outline" onClick={reset} data-testid="etifood-back-btn">Indietro</Button>}
          {step === 2 && (
            <Button onClick={confirm} disabled={busy} className="bg-[#8A1C2A] hover:bg-[#701622]" data-testid="etifood-confirm-btn">
              {busy ? "Import in corso…" : "Conferma import"}
            </Button>
          )}
          {step === 3 && <Button onClick={close} className="bg-[#8A1C2A] hover:bg-[#701622]" data-testid="etifood-done-btn">Chiudi</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EtifoodImportDialog;
